<template>
  <div
    class="BuyBox"
    :class="{
      'mobOnly:mb-48': design !== 'quickbuy',
    }"
  >
    <ConfirmModal
      v-if="showConfirmAddToCart"
      :show-abort="true"
      @close="showConfirmAddToCart = false"
      @click-outside="showConfirmAddToCart = false"
      @confirm="addToCart"
    >
      <div class="p-32 pb-24 type-lg">
        {{ $t('productPage.buybox.confirmBackorderTitle', { date: estimatedDeliveryDate }) }}
      </div>
      <div class="wysiwyg pr-32 pl-24 pb-32">
        <ul>
          <li>
            {{ $t('productPage.buybox.confirmBackorderContentFirst') }}
          </li>
          <li>
            {{ $t('productPage.buybox.confirmBackorderContentSecond') }}
          </li>
        </ul>
      </div>
    </ConfirmModal>

    <!-- old norce discount variant -->
    <MultiBuyDiscount
      v-if="!userStore.isSamplesOnlyUser && !userStore.isMediabankUser && !props.activeVariant.isPurchaseDisabled"
      :key="activeVariant.partNo"
      :active-variant="activeVariant"
      @change-campaign-type="changeCampaignType"
    />

    <!-- new quantity-breaks api -->
    <ShowDiscountQuantityBreaks
      v-if="userStore.isLoggedIn && !userStore.isSamplesOnlyUser && !userStore.isMediabankUser && !props.activeVariant.isPurchaseDisabled"
      :discount-quantity-breaks="discountQuantityBreaks"
      :categories="productItem.categories"
    />

    <div v-if="!activeVariant.productHasExpired && !userStore.isSamplesOnlyUser && !userStore.isMediabankUser && !props.activeVariant.isPurchaseDisabled" class="type-tagline-xs text-text-subtle">
      <div
        class="mb-8"
        :class="{
          'hidden': disabledQuantityBtns && !shouldUseArea,
        }"
      >
        {{ $t('productPage.sizeSelector.quantityLabel') }}
      </div>

      <!-- * * * * * if product should be calculated in length/area -->
      <PackageToAreaConverter
        v-if="shouldUseArea"
        v-model="totalQuantity"
        :input="activeVariant"
      />

      <!-- * * * * * standard quantity selector -->
      <div v-else-if="!disabledQuantityBtns" class="flex justify-left gap-8 desk:gap-12 items-end select-none">
        <div class="border border-light flex flex-row h-40">
          <button
            class="w-40 h-40 flex items-center justify-center group"
            :class="{ 'pointer-events-none': totalQuantity === 1}"
            :disabled="totalQuantity === subOrAddQuantity"
            type="button"
            @click="totalQuantity = (totalQuantity - subOrAddQuantity)"
          >
            <img
              src="/icons/minus.svg"
              class="w-16 h-16 bg-lightest outline outline-4 desk:outline-8 outline-lightest
              group-hover:bg-brandLight group-hover:outline-brandLight transition-all"
              alt=""
            >
          </button>
          <div
            class="w-32 flex items-center justify-center type-headline-xs desk:type-headline-sm"
          >
            {{ totalQuantity }}
          </div>
          <button
            class="w-40 h-40 flex items-center justify-center group"
            :class="{
              'pointer-events-none': totalQuantity >= canBuyMoreAmount && canBuyMoreAmount > 0 || canBuyMoreAmount === 0 && cartStore.getQuantityFromPartNo(activeVariant.partNo) > 0
            }"
            :disabled="!globalContent.getAllowBackorders && totalQuantity + subOrAddQuantity > canBuyMoreAmount && !activeVariant.isOnDemandProduct"
            type="button"
            @click="totalQuantity = (totalQuantity + subOrAddQuantity)"
          >
            <img
              src="/icons/plus.svg"
              class="
        w-16 h-16 outline outline-4 desk:outline-8 outline-transparent transition-all
        group-hover:bg-brandLight group-hover:outline-brandLight"
              alt=""
            >
          </button>
        </div>
        <template v-if="!isD2CProduct">
          <div
            v-for="bulkOption in [6, 12, 24]"
            :key="'bulk-option-' + bulkOption"
            class="w-auto h-40 shrink-0 type-headline-sm"
          >
            <button
              class="bg-lighter px-16 h-40 hover:bg-brandLight transition-all"
              :class="{'pointer-events-none': !globalContent.getAllowBackorders && !activeVariant.isOnDemandProduct && !activeVariant.isPreOrderProduct && canBuyMoreAmount < bulkOption}"
              :disabled="!globalContent.getAllowBackorders && !activeVariant.isOnDemandProduct && !activeVariant.isPreOrderProduct && canBuyMoreAmount < bulkOption"
              type="button"
              @click="totalQuantity = (totalQuantity = bulkOption)"
            >
              +{{ bulkOption }}
            </button>
          </div>
        </template>
      </div>
    </div>
    <div v-if="globalContent.getPurchasesEnabled && !userStore.isSamplesOnlyUser && !userStore.isMediabankUser && !props.activeVariant.isPurchaseDisabled">
      <!-- "can not add more in cart" -->
      <div v-if="!productItem.shouldCheckFutureAvailability && !globalContent.getAllowBackorders && !activeVariant.isOnDemandProduct" class="type-sm-medium mt-8">
        <!-- already on max -->
        <div
          v-if="canBuyMoreAmount === 0 && cartStore.getQuantityFromPartNo(activeVariant.partNo) > 0"
          class="inline-flex relative p-12 items-center gap-6 bg-warningLight"
        >
          <img
            src="/icons/info.svg"
            class="w-16 h-16"
            alt="info"
          >
          {{ $t('stock.maxInCart') }}
        </div>
        <!-- more than max -->
        <div
          v-else-if="totalQuantity >= canBuyMoreAmount && canBuyMoreAmount > 0"
          class="inline-flex relative p-12 items-center gap-6 bg-warningLight"
        >
          <div class="w-16 h-16 rounded-1 rotate-45 bg-warningLight absolute -top-4 left-48" />
          <img
            src="/icons/info.svg"
            class="w-16 h-16"
            alt="info"
          >
          {{ $t('stock.maxCanAdd', { num: canBuyMoreAmount }) }}
        </div>
      </div>
    </div>
    <div v-if="!userStore.isSamplesOnlyUser && !userStore.isMediabankUser && !props.activeVariant.isPurchaseDisabled" class="flex items-center flex-wrap mt-8 mb-16 min-h-20">
      <div v-if="!globalContent.getShowLoginForPrice" class="items-center type-sm mr-16">
        <StockStatusSwitcher
          :key="activeVariant.partNo"
          :total-quantity="totalQuantity"
          :product-item="productItem"
          :active-variant="activeVariant"
          @loading="e => loadingFromFutureStockApi = e"
          @set-buy-button-disabled="setDisableBuyFromFutureStockApi"
        />
      </div>
      <!-- Area / Quantity per pallet -->
      <div v-if="palletQuantityInfo && globalContent.getCountryCode !== 'us'" class="type-sm flex items-center">
        <img
          src="/icons/package.svg"
          class="w-16 h-16 mr-6"
          alt="info"
        >
        {{ $t('productPage.pallet') }}: {{ palletQuantityInfo.area }} / {{ palletQuantityInfo.packages }}
      </div>
    </div>
    <!-- anchor link to related products -->
    <div v-if="hasRelatedProducts && design !== 'quickbuy' && !userStore.isSamplesOnlyUser && !userStore.isMediabankUser" class="mb-12">
      <a href="#pdpRelatedProducts" class="underline">
        {{ $t('pdp.link.alternative') }}
      </a>
    </div>

    <!-- * * * * notify when back in stock -->
    <div v-if="!globalContent.getAllowBackorders && activeVariant && !activeVariant.canAddToCart && !activeVariant.productHasExpired && globalContent.getPurchasesEnabled && !isResilientProduct && !userStore.isSamplesOnlyUser && !userStore.isMediabankUser && !props.activeVariant.isPurchaseDisabled">
      <div v-if="!stockAlertSuccess" class="relative h-48 mb-12">
        <TextInput
          v-model="outOfStockEmail"
          type="email"
          class="input h-48"
          :label="$t('productPage.stockAlertPlaceholder')"
          @keyup.enter="subscribeToStockUpdates"
        />
      </div>
      <div v-if="stockAlertError" class="text-criticalDark text-14 mb-12">
        {{ $t('productPage.stockAlertError') }}
      </div>
      <div
        v-if="stockAlertSuccess"
        class="type-headline-sm text-center mb-12 p-24"
        :class="{
          'bg-successPrimary text-lightest': lastResponseCode !== 200,
          'bg-lighter': lastResponseCode === 200}"
      >
        {{ stockAlertSuccessText ?? $t('productPage.stockAlertSuccess') }}
      </div>
    </div>

    <!-- * * * * active product -->
    <div v-else-if="!userStore.isSamplesOnlyUser && !userStore.isMediabankUser && !props.activeVariant.isPurchaseDisabled">
      <div v-if="globalContent.getEnableOrderProposalRequest && showOrderProposalRequest && userStore.isLoggedIn && !userStore.isSalesRepUser" class="mb-16 w-full">
        <button
          class="btn btn--lg btn--secondary w-full mb-8"
          :class="{
            'loading': requestProposalLoading,
          }"
          type="button"
          @click="onClickRequestOrderProposal"
        >
          {{ $t('productPage.requestOrderProposalButton') }}
        </button>
        <div class="flex desk:items-center justify-center">
          <img
            src="/icons/info-subtle.svg"
            class="w-16 h-16 mr-6"
            alt="info"
          >
          <p class="type-xs text-dark leading-single">{{ $t('productPage.requestOrderProposalDescription') }}</p>
        </div>
      </div>
      <div v-if="!stockAlertSuccess" class="flex">
        <button
          v-if="globalContent.getShowLoginForPrice && !globalContent.getIsGlobalMarket"
          class="btn btn--lg flex-1 w-full btn"
          type="button"
          @click.prevent="showLogin"
        >
          {{ $t('priceInfo.loginForPriceInfo') }}
        </button>
        <!-- logged in but cant be added to cart -->
        <button
          v-else-if="!globalContent.getAllowBackorders && !props.activeVariant.canAddToCart && !isResilientProduct"
          class="btn btn--lg flex-1 w-full mb-24 btn--secondary"
          :disabled="!validNotifyMail"
          type="button"
          @click="subscribeToStockUpdates"
        >
          {{ $t('notify.openModalButton') }}
        </button>

        <Tooltip
          v-else-if="userStore.isFinanceUser"
          :show-on-hover="true"
          class="ml-8 w-full"
          :text="$t('priceInfo.financeUserTooltip')"
        >
          <button
            class="btn btn--lg flex-1 w-full mb-24 btn"
            :class="{
              'pointer-events-none': true,
            }"
            type="button"
            :disabled="true"
          >
            {{ addToCartLabel }}
            <span
              v-if="totalSum > 0"
              class="ml-8 text-12 normal-case"
            >
              {{ useLocalizedCurrency(totalSum) }}
            </span>
          </button>
        </Tooltip>

        <!-- * * * * actual buy button -->
        <button
          v-else-if="canPurchaseProduct && (globalContent.getIsB2b || isAccessoriesProduct || (isD2CProduct && globalContent.getNorceCheckoutEnabled)) && !globalContent.getIsGlobalMarket"
          class="btn btn--lg flex-1 w-full btn"
          :class="{
            'loading': loading || loadingFromFutureStockApi,
            'pointer-events-none': disabledBuyButton,
          }"
          :disabled="disabledBuyButton || disableBuyFromFutureStockApi"
          type="button"
          @click="maybeConfirmAddToCart"
        >
          <img src="/icons/cart-inv.svg" class="inline-block w-20 mr-16">
          <span>{{ addToCartLabel }}</span>
          <span
            v-if="totalSum > 0 && !uiStore.hidePrices"
            class="ml-6"
          >–
          </span>
          <span
            v-if="totalSum > 0 && !uiStore.hidePrices"
            class="ml-8"
          >
            {{ useLocalizedCurrency(totalSum) }}
          </span>
          <span
            v-if="totalSum > 0 && reachedDiscountPercent > 0 && !uiStore.hidePrices"
            class="ml-8 opacity-50 line-through"
          >
            {{ useLocalizedCurrency(totalBasePrice) }}
          </span>
        </button>

        <!-- * * * * favorite button -->
        <button
          v-if="userStore.canFavorizeProducts"
          class="w-48 h-48 px-12 ml-12 shrink-0 border hover:bg-lightest hover:border-darkest transition-all select-none"
          :class="{
            'bg-bg-base border-border-inverse' : !isFavorite,
            'bg-lightest border border-light' : isFavorite,
          }"
          type="button"
          @click="addOrDeleteFavorite"
        >
          <img
            alt=""
            :src="showStar ? '/icons/heart-filled.svg' : '/icons/heart.svg'"
            class="h-24 w-24"
            :class="{
              'favoriteAnimateIn': loadingToTrue,
            }"
          >
        </button>
      </div>
    </div>

    <!-- * * * * order samples
    Don't show samples if we are already on a sample page (SA0 for most markets, US0 for US)
    For samplesOnlyUser: only show if samples are available (pre-loaded check)
    For regular users: show if product has sample partNos (lazy load on click)
    -->
    <div v-if="!userStore.isMediabankUser && ((userStore.isSamplesOnlyUser && samplesHydrated.length > 0) || (!userStore.isSamplesOnlyUser && hasSamplePartNos)) && userStore.isLoggedIn && !activeVariant.partNo?.startsWith('SA0') && !activeVariant.partNo?.startsWith('US0') && !globalContent.getIsGlobalMarket" class="mt-16">
      <button
        class="btn btn--secondary w-full btn--lg"
        :class="{
          loading: loadingSamples,
        }"
        :disabled="loadingSamples"
        type="button"
        @click="openSamples"
      >
        {{ $t('samples.cta') }}
      </button>
      <transition name="fromRight">
        <QuickBuyMultiple
          v-if="showSampleQuickbuy"
          :products-in="samplesHydrated"
          category-code="Samples"
          @close="showSampleQuickbuy=false"
        />
      </transition>
    </div>

    <!-- * * * * B2B Non-Logged-In Sample Button -->
    <div v-if="showB2bNonLoggedInSampleButton && !globalContent.getIsGlobalMarket" class="mt-16">
      <button
        class="btn btn--secondary w-full btn--lg"
        :class="{ loading: loadingB2bSample }"
        :disabled="loadingB2bSample"
        type="button"
        @click="addB2bSampleToCart"
      >
        <span v-if="cartStore.getQuantityFromPartNo(b2bSamplePartNo) > 0">
          {{ $t('quickbuy.button.inCart') }}
        </span>
        <span v-else>
          {{ $t('samples.b2b.orderSample') }}
        </span>
      </button>
    </div>

    <!-- * * * * disclaimer about meter/m2 -->
    <div v-if="shouldUseArea && design !== 'quickbuy' && showWasteDisclaimer" class="flex p-16 gap-8 bg-bg-attention type-headline-sm mt-24 items-start">
      <img src="/icons/info.svg" class="w-16 relative top-2">
      <div>{{ disclaimerText }}</div>
    </div>

    <RequestOrderProposalModal
      v-if="showRequestOrderProposalModal"
      @close="showRequestOrderProposalModal = false"
    />
  </div>
</template>

<script lang="ts" setup>
import type { ProductModel } from '#models/product';
import Product from '#models/product';
import { useCartStore } from '~/store/cart';
import { useUserStore } from '~/store/user';
import { useGlobalContentStore } from '~/store/globalContent';
import { useProductStore } from '~/store/product';
import { useUiStore } from '~/store/ui';
import ProductRelatedProductsList from '~/components/product-page/ProductRelatedProductsList.vue';
import type { ProductVariantModel } from '#models/productVariant';
import type ProductVariant from '#models/productVariant';
import useApiFetch from '~/composables/useApiFetch';
import { NorceRelatedProductTypeCodes } from '#constants/norceCodes';
import MultiBuyDiscount from '~/components/product-page/MultiBuyDiscount.vue';
import Tooltip from '~/components/body/Tooltip.vue';
import type { CampaignItem } from '#constants/types/multiDiscount';
import useMultiDiscount from '~/composables/useMultiDiscount';
import TextInput from '~/components/form-elements/TextInput.vue';
import ConfirmModal from '~/components/modals/ConfirmModal.vue';
import usePackageAreaConverter from '~/composables/usePackageAreaConverter';
import PackageToAreaConverter from '~/components/product-page/PackageToAreaConverter.vue';
import type { DiscountQuantityBreak, DiscountQuantityBreakApiResponse } from '#constants/types/norce';
import NP from 'number-precision';
import ShowDiscountQuantityBreaks from '~/components/product-page/ShowDiscountQuantityBreaks.vue';
import { useLocalizedCurrency } from '~/composables/useLocalizedCurrency';
import QuickBuyMultiple from '~/components/product/QuickBuyMultiple.vue';
import useAlgoliaFetch from '~/composables/useAlgoliaFetch';
import StockStatusSwitcher from '~/components/product/StockStatusSwitcher.vue';
import RequestOrderProposalModal from '~/components/modals/RequestOrderProposalModal.vue';
import useWarehouse from '~/composables/useWarehouse';
import useProductPurchase from '~/composables/useProductPurchase';

NP.enableBoundaryChecking(false);

const { apiGet } = useApiFetch();

const userStore = useUserStore();
const uiStore = useUiStore();
const cartStore = useCartStore();
const productStore = useProductStore();
const globalContent = useGlobalContentStore();
const { getVariantStockValue } = useWarehouse();
const { canPurchase } = useProductPurchase();
const { $t } = useNuxtApp();
const totalQuantity = ref(1);
const totalQuantityChanged = ref(false); // if user changed totalquantity
const subOrAddQuantity = ref(1);
const relatedList = ref<ProductModel[]>([]);
const { getExactVariantByPartNo, lastError, lastErrorCode } = useAlgoliaFetch();
const showRequestOrderProposalModal = ref(false);

interface Props {
  activeVariant: ProductVariantModel & { name: string },
  productItem: ProductModel,
  disabled?: boolean
  crossell?: boolean,
  design: 'standard' |'quickbuy',
  pageType?: 'b2b' | 'b2c'
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  crossell: false,
  design: 'standard',
  pageType: 'b2c',
});

const {
  percentageCampaigns,
  hasCampaign,
  giftCampaigns,
} = useMultiDiscount({ activeVariant: props.activeVariant });

const {
  unitDisplay,
  shouldUseArea,
  areaPerPackage,
  convertPackagePriceToAreaPrice,
} = usePackageAreaConverter(toRef(()=>props.activeVariant));

const priceListLocked = ref(hasCampaign && !giftCampaigns.value.length);

const disabledQuantityBtns = computed(() => {
  if (isD2CProduct.value) {
    return getVariantStockValue(props.activeVariant) <= 0;
  }
  return (!userStore.isLoggedIn || props.activeVariant.isSoldOut)
    && !props.activeVariant.isOnDemandProduct
    && !props.activeVariant.isPreOrderProduct
    && !globalContent.getAllowBackorders
    && !props.productItem.shouldCheckFutureAvailability;
});

const canBuyMoreAmount = computed<number>(()=> {
  if (props.productItem.shouldCheckFutureAvailability && !isD2CProduct.value) {
    return Infinity;
  }
  const cartQty = cartStore.getQuantityFromPartNo(props.activeVariant.partNo);
  const stockValue = getVariantStockValue(props.activeVariant);
  const qty = stockValue - cartQty;

  if (props.activeVariant.hasFixedRecommendedQty && (props.activeVariant.recommendedQty && qty > props.activeVariant.recommendedQty)) {
    if ((cartQty + totalQuantity.value) > props.activeVariant.recommendedQty) {
      return 0;
    }
    return props.activeVariant.recommendedQty;
  }

  return qty;
});

const disabledBuyButton = computed(() => {
  if (userStore.isFinanceUser) {
    return true;
  } else if (props.disabled) {
    return true;
  } else if (props.productItem.productUnavailable) {
    return true;
  } else if (isD2CProduct.value) {
    return getVariantStockValue(props.activeVariant) <= 0;
  } else if (globalContent.getAllowBackorders) {
    return false;
  } else if (props.activeVariant.isInStock || props.activeVariant.isPreOrderProduct || props.activeVariant.isOnDemandProduct || props.activeVariant.hasIncomingDate || props.activeVariant.isSoldOut) {
    return false;
  }

  return true;
});

const addToCartLabel = computed(() => {
  if (globalContent.getAllowBackorders || props.activeVariant.shouldCheckFutureAvailability) {
    return $t('productPage.sizeSelector.addToCartBtn'); // add to cart
  } else if (props.activeVariant.isPreOrderProduct) {
    return $t('productPage.sizeSelector.preOrder'); // preorder
  } else if (props.activeVariant.isTempSoldOut) {
    return $t('productPage.sizeSelector.watchStockBtn'); // add to watch
  } else if (props.activeVariant.isOnDemandProduct || (props.activeVariant && props.activeVariant.canAddToCart) || props.activeVariant.hasIncomingDate) {
    return $t('productPage.sizeSelector.addToCartBtn'); // add to cart
  }
  return $t('productPage.sizeSelector.watchStockBtn'); // add to watch
});

const totalSum = computed((): number => {
  if ((!globalContent.getAllowBackorders && props.activeVariant.canAddToCart) || (globalContent.getAllowBackorders && !props.activeVariant.productHasExpired)) {
    if (userStore.getPrice(props.activeVariant.partNo, props.crossell).status === 'ok') {
      const priceData = userStore.getPrice(props.activeVariant.partNo, props.crossell).price;
      const basePrice = priceListLocked.value
        ? Number(globalContent.getDisplayIncVat ? priceData?.priceBeforeDiscount : priceData?.priceBeforeDiscountBeforeVat) ?? 0
        : Number(globalContent.getDisplayIncVat ? priceData?.price : priceData?.priceBeforeVat) ?? 0;

      // discounts from api
      if (reachedDiscountPrice.value) {
        return totalQuantity.value * reachedDiscountPrice.value;
      }
      // discounts from norce
      else if (percentageCampaigns.value.length > 0) {
        const highestLimitReachedTrue = percentageCampaigns.value
          .filter(item => totalQuantity.value >= item.limit)
          .reduce<CampaignItem | null>((acc, item) => {
            return !acc || item.limit > acc.limit ? item : acc;
          }, null);

        return highestLimitReachedTrue ? totalQuantity.value * basePrice * ((100 - highestLimitReachedTrue.gainedAmount) / 100) : totalQuantity.value * basePrice;
      } else {
        return totalQuantity.value * basePrice;
      }
    }
  }

  return 0;
});

// Price * Qty without any discount
const totalBasePrice = computed((): number => {
  if ((!globalContent.getAllowBackorders && props.activeVariant.canAddToCart) || (globalContent.getAllowBackorders && !props.activeVariant.productHasExpired)) {
    if (userStore.getPrice(props.activeVariant.partNo, props.crossell).status === 'ok') {
      const priceData = userStore.getPrice(props.activeVariant.partNo, props.crossell).price;
      const basePrice = priceListLocked.value
        ? Number(globalContent.getDisplayIncVat ? priceData?.priceBeforeDiscount : priceData?.priceBeforeDiscountBeforeVat) ?? 0
        : Number(globalContent.getDisplayIncVat ? priceData?.price : priceData?.priceBeforeVat) ?? 0;

      return totalQuantity.value * basePrice;
    }
  }

  return 0;
});

const showOrderProposalRequest = computed(() => {
  const currentProductArea = areaPerPackage.value ? (areaPerPackage.value * totalQuantity.value) : 0;
  const totalArea = cartStore.cartTotalArea + currentProductArea;

  return totalArea >= globalContent.orderProposalRequestAreaThreshold;
});

const showWasteDisclaimer = computed(() => {
  return props.productItem.categories?.some(category => category.code?.toLowerCase() === 'floors') || false;
});

const disclaimerText = computed(() => {
  if (!showWasteDisclaimer.value) {
    return '';
  }

  const productDesign = props.productItem.parametricByCode('ProductDesign');

  // Check if its Herringbone or Chevron from Norce
  if (productDesign?.multipleValues && productDesign.multipleValues.length > 0) {
    const designCode = productDesign.multipleValues[0].code;
    if (designCode === 'Herringbone' || designCode === 'Chevron') {
      return $t('areaConvert.disclaimerHerringboneChevron');
    }
  }

  return $t('areaConvert.disclaimer');
});

const isResilientProduct = computed(() => {
  return props.productItem.categories?.some(f => f.code === 'Resilient' || f.code === 'ResilientAccessories') ?? false;
});

const isAccessoriesProduct = computed(() => {
  return props.productItem.categories?.some(f => f.code === 'Accessories') ?? false;
});

const isD2CProduct = computed(() => {
  return props.productItem.isD2C;
});

/**
 * Check if user can purchase this product based on pricelists
 * For D2C products, always allow (B2C doesn't use pricelist restrictions)
 */
const canPurchaseProduct = computed(() => {
  if (props.activeVariant.isPurchaseDisabled) {
    return false;
  }
  if (userStore.isMediabankUser) {
    return false;
  }
  if (isD2CProduct.value) {
    return true;
  }
  return canPurchase(props.productItem);
});

const showLogin = () => {
  uiStore.setShowSigninModal(true, true);
};

const emit = defineEmits<{
  (e: 'changeSize', size: ProductVariant & { name: string }): void;
  (e: 'addToCart'): void;
  (e: 'addingToCart'): void;
}>();

const outOfStockEmail = userStore.isLoggedIn ? ref(userStore.userProfile.email) : ref('');

const { apiPost, lastResponseCode } = useApiFetch();
const loadingStockAlert = ref(false);
const stockAlertSuccess = ref(false);
const stockAlertSuccessText = ref<string | null>(null);
const stockAlertError = ref(false);
const subscribeToStockUpdates = async() => {
  if (validNotifyMail.value) {
    loadingStockAlert.value = true;
    stockAlertError.value = false;
    stockAlertSuccess.value = false;
    stockAlertSuccessText.value = null;
    const res = await apiPost(`/products/${props.activeVariant.partNo}/stock-alerts`, {
      email: outOfStockEmail.value,
    });
    if (res) {
      stockAlertSuccess.value = true;
      if (lastResponseCode.value === 200) {
        stockAlertSuccessText.value = $t('productPage.stockAlertSuccessAgain');
      }

      setTimeout(()=> stockAlertSuccess.value = false, 10000);
    } else {
      stockAlertError.value = true;
    }

    loadingStockAlert.value = false;
  } else {
    console.warn('not valid');
  }
};

const validNotifyMail = computed(()=> {
  const regex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;
  return regex.test(outOfStockEmail.value);
});

const loading = ref(false);
// Same as loading but while future stock api
const loadingFromFutureStockApi = ref(false);
const showConfirmAddToCart = ref(false);

// If we have an expiring product, that wan't return, we disable buy button
const disableBuyFromFutureStockApi = ref(false);
const setDisableBuyFromFutureStockApi = (set: boolean) => {
  if (props.activeVariant.isExpiring) {
    disableBuyFromFutureStockApi.value = set;
  }
};

const maybeConfirmAddToCart = async() => {
  if (isBackorderProduct.value) {
    showConfirmAddToCart.value = true;
  } else {
    await addToCart();
    // Scroll to ProductAccessories
    if (props.design === 'standard') {
      const section = document.querySelector('.ProductAccessories');
      if (section) {
        const offset = 88;
        const elementPosition = section.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - offset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }
    }
  }
};

const addToCart = async() => {
  loading.value = true;
  emit('addingToCart');

  if (
    (!globalContent.getAllowBackorders &&
          (props.activeVariant.canAddToCart || props.activeVariant.hasIncomingDate)) ||
      (globalContent.getAllowBackorders && !props.activeVariant.productHasExpired)
  ) {
    await cartStore.updateCart(
      props.activeVariant.partNo,
      totalQuantity.value,
      -1,
      !uiStore.quickBuyCloseAfterAdd,
      props.activeVariant,
      false,
      props.productItem.objectID,
      priceListLocked.value
    );
    emit('addToCart');
  }

  loading.value = false;
};

const loadingToTrue = ref(false);
const loadingToFalse = ref(false);

const addOrDeleteFavorite = async() => {
  if (isFavorite.value) {
    loadingToFalse.value = true;
    await userStore.updateFavoriteProductQuantity(props.activeVariant.partNo, 0);
    loadingToFalse.value = false;
  } else {
    loadingToTrue.value = true;
    await userStore.saveFavoriteProduct(props.activeVariant.partNo, totalQuantity.value);
    loadingToTrue.value = false;
  }
};

const showStar = computed(()=> {
  return (isFavorite.value && !loadingToFalse.value) || loadingToTrue.value;
});

const isFavorite = computed((): boolean => {
  return !!userStore.userProfile.favoriteProducts?.find(favorite => favorite.id === props.activeVariant.partNo);
});

const isBackorderProduct = computed(() => {
  return globalContent.getAllowBackorders && !props.activeVariant.isInStock;
});

const estimatedDeliveryDate = computed(() => {
  return props.activeVariant.incomingDate;
});

watch(
  () => props.activeVariant,
  () => {
    totalQuantity.value = 1;
    totalQuantityChanged.value = false;
    stockAlertError.value = false;
    stockAlertSuccess.value = false;
    if (props.activeVariant.productHasExpired) {
      setRelatedList();
    }
  }
);

onMounted(() => {
  if (props.activeVariant.productHasExpired) {
    setRelatedList();
  }
});

const setRelatedList = (async() => {
  const partNos = props.productItem.relatedProductsByType(NorceRelatedProductTypeCodes.Replacer);
  if (partNos.length > 0) {
    relatedList.value = await productStore.getRelatedProducts(partNos);
  }
});

const changeCampaignType = (type: boolean) => {
  if (!giftCampaigns.value.length) {
    priceListLocked.value = type;
  }
};

const requestProposalLoading = ref(false);
const onClickRequestOrderProposal = async() => {
  requestProposalLoading.value = true;
  await cartStore.updateCart(
    props.activeVariant.partNo,
    totalQuantity.value,
    -1,
    false,
    props.activeVariant,
    false,
    props.productItem.objectID,
    priceListLocked.value
  );
  // Open order proposal modal
  showRequestOrderProposalModal.value = true;
  requestProposalLoading.value = false;
};

watch(totalQuantity, () => {
  totalQuantityChanged.value = true;
});

watch(() => props.activeVariant, ()=> {
  checkDiscountLevels();
});

// * * * * * * * * checking quantity breaks discounts
const discountCBraw = ref<DiscountQuantityBreakApiResponse[]>([]);
const checkDiscountLevels = async() => {
  const res = await apiGet<DiscountQuantityBreakApiResponse[]>(`/products/${props.activeVariant.partNo}/quantity-breaks`);
  if (res) {
    discountCBraw.value = res;
  } else {
    console.warn(`Couldn't load quantity-breaks on ${props.activeVariant.partNo}`);
  }
};

const discountQuantityBreaks = computed<DiscountQuantityBreak[]>(()=> {
  return discountCBraw.value.map((i: DiscountQuantityBreakApiResponse) => {

    return {
      ...i,
      // if we reach the step by combining whats in the cart and whats about to be added
      reached: (totalQuantity.value + cartStore.getQuantityFromPartNo(props.activeVariant.partNo)) >= i.quantity,
      areaPrice: convertPackagePriceToAreaPrice(globalContent.getDisplayIncVat ? i.price : i.priceBeforeVat),
      unitDisplay,
      areaPerPackage: areaPerPackage.value,
    };
  });
});

const reachedDiscountPercent = computed(()=> {
  const percent = discountQuantityBreaks.value.filter(f => f.reached === true).map(m => m.discountPercentage);
  percent.push(0);
  return Math.max(...percent);
});

const reachedDiscountPrice = computed(()=> {
  uiStore.overrideDisplayPrice = -1;
  const reached = discountQuantityBreaks.value.filter(f => f.reached === true)
    .map(m => {
      const priceString = globalContent.getDisplayIncVat ? m.price : m.priceBeforeVat;
      return parseFloat(priceString.replaceAll(',', ''));
    });
  if (!reached.length) {
    return null;
  }

  const newPrice = Math.min(...reached);
  uiStore.overrideDisplayPrice = newPrice;
  return newPrice;
});

const palletQuantityInfo = computed(() => {
  const palletAmount = props.activeVariant.parametricByCode('ItemPalletAmount');
  const palletAreaM2 = props.activeVariant.parametricByCode('ItemAreaPerPalletM2');

  if (palletAmount?.value && palletAreaM2?.value) {
    let areaValue;

    if (globalContent.getMeasurement === 'imperial') {
      areaValue = palletAreaM2.value * 10.7639; // convert to ft²
    } else {
      areaValue = palletAreaM2.value;
    }

    return {
      packages: `${palletAmount.value} ${$t('units.package.short')}`,
      area: `${areaValue.toFixed(2)} ${unitDisplay}`,
    };
  }

  return null;
});

onMounted(()=> {
  checkDiscountLevels();
});

/**
 * Unfortunatly we have to load the samples to see if we even should show the button
 * since it's possible it's not on the users pricelist
 */
const samplesHydrated = ref<ProductModel[]>([]);
const showSampleQuickbuy = ref(false);
const loadingSamples = ref(false);
const samplePartNos = computed(() => {
  const samples = props.activeVariant.relatedProducts
    .filter(f => f.code === NorceRelatedProductTypeCodes.Samples)
    .map(m => m.partNo);

  // Expand US01 to include US02 for US market
  if (samples.includes('US01') && !samples.includes('US02')) {
    samples.push('US02');
  }

  return samples;
});
// Only SA01/SA02/SA03 (or US01/US02 for US market) count as orderable samples, matching the loadSamples() filter below
const hasSamplePartNos = computed(() => {
  if (globalContent.domainMap?.countryCode === 'us') {
    return samplePartNos.value.some(p => p?.toUpperCase().startsWith('US01') || p?.toUpperCase().startsWith('US02'));
  }
  return samplePartNos.value.some(p =>
    p?.toUpperCase().startsWith('SA01') ||
    p?.toUpperCase().startsWith('SA02') ||
    p?.toUpperCase().startsWith('SA03')
  );
});

// B2B non-logged-in sample button logic — only SA01 (or SA03 for resilient, US01 for US market), same restriction as B2C
const b2bSamplePartNo = computed(() => {
  const isResilient = props.productItem.categories?.some(
    f => f.code === 'Resilient' || f.code === 'ResilientAccessories'
  );
  const samples = props.activeVariant.relatedProducts
    .filter(f => f.code === NorceRelatedProductTypeCodes.Samples)
    .map(m => m.partNo);

  if (isResilient) {
    return samples.find(s => s?.toUpperCase().startsWith('SA03'));
  }
  if (globalContent.domainMap?.countryCode === 'us') {
    return samples.find(s => s?.toUpperCase().startsWith('US01'));
  }
  return samples.find(s => s?.toUpperCase().startsWith('SA01'));
});

const showB2bNonLoggedInSampleButton = computed(() => {
  return globalContent.getIsB2b &&
         !userStore.isLoggedIn &&
         !!b2bSamplePartNo.value &&
         !props.activeVariant.partNo?.startsWith('SA0');
});

const loadingB2bSample = ref(false);
const addB2bSampleToCart = async() => {
  if (!b2bSamplePartNo.value) { return; }
  if (cartStore.getQuantityFromPartNo(b2bSamplePartNo.value) > 0) {
    const { manualUrl } = useLinkReplacer();
    navigateTo(manualUrl('/checkout'));
  } else {
    loadingB2bSample.value = true;
    await cartStore.updateCart(b2bSamplePartNo.value, 1, -1, true);
    loadingB2bSample.value = false;
  }
};

const loadSamples = async() => {
  if (loadingSamples.value || !hasSamplePartNos.value) {
    return;
  }
  loadingSamples.value = true;
  const res = await getExactVariantByPartNo(samplePartNos.value);
  if (res) {
    const allSamples = res.hits.map(hit => Product.create([hit]));
    // Filter to only show SA01, SA02, SA03 samples (or US01/US02 for US market)
    if (globalContent.domainMap?.countryCode === 'us') {
      samplesHydrated.value = allSamples.filter((s) =>
        s.partNo?.toUpperCase().startsWith('US01') || s.partNo?.toUpperCase().startsWith('US02')
      );
    } else {
      samplesHydrated.value = allSamples.filter((s) =>
        s.partNo?.toUpperCase().startsWith('SA01') ||
        s.partNo?.toUpperCase().startsWith('SA02') ||
        s.partNo?.toUpperCase().startsWith('SA03')
      );
    }
  } else {
    console.log(lastError.value);
    console.log(lastErrorCode.value);
  }
  loadingSamples.value = false;
};

const openSamples = async() => {
  if (samplesHydrated.value.length === 0) {
    await loadSamples();
  }
  if (samplesHydrated.value.length > 0) {
    showSampleQuickbuy.value = true;
  }
};

watch(
  () => props.activeVariant.partNo,
  () => {
    samplesHydrated.value = [];
    showSampleQuickbuy.value = false;
    loadingSamples.value = false;
    // Pre-load samples for samplesOnlyUser to check availability (they may have pricelist restrictions)
    if (hasSamplePartNos.value && userStore.isSamplesOnlyUser) {
      loadSamples();
    }
  }
);

// Pre-load samples on mount for samplesOnlyUser to check availability
onMounted(() => {
  if (hasSamplePartNos.value && userStore.isSamplesOnlyUser) {
    loadSamples();
  }
});

const hasRelatedProducts = computed(()=> {
  return props.activeVariant.relatedProducts.length > 0;
});

</script>
